// @ts-check
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { Select, Icon, DatePicker } from 'components/common';
import { Tooltip } from 'antd';
import { setFilterGroup } from 'actions/organisation';
import { getFilter } from 'selectors/organisation';
import { commonDateRanges } from 'services/inputTokens';

// SCIL-17770: the shared "Date" dropdown for the People and Activity tabs. It
// reads/writes the shared `date` key on the org `filters[orgId]` slice, so both
// tabs stay in sync. Rendered by OrgResultsHeader, under the "Search Results"
// heading, since it describes the results rather than the query.

const DatePane = styled.div`
  position: relative;
  margin-top: 10px;
  display: flex;
  align-items: center;
  width: fit-content;
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 400;
  font-family: 'Roboto';
  color: rgba(0, 0, 0, 0.85);
  margin-right: 6px;
`;

const InfoItem = styled.div`
  position: absolute;
  top: -10px;
  right: -5px;
`;

const RangePane = styled.div`
  display: inline-block;
  margin-left: 6px;
`;

// A picked range is stored as its [from, to] array, which the dropdown shows as Custom.
export const getDateOption = date => (Array.isArray(date) ? 'custom' : date);

const OrgDateFilter = ({
  organisationId,
  loading,
  onChange,
  showResetInfo,
}) => {
  const dispatch = useDispatch();
  const date = useSelector(getFilter(organisationId, 'date'));
  const dateOption = getDateOption(date);

  const handleDateChange = value => {
    // Clearing the range picker goes back to All Time, as on main search.
    const next = value ?? 'all';
    dispatch(setFilterGroup(organisationId, 'date', next));
    // Custom on its own filters nothing: search once both dates are picked.
    if (next !== 'custom') {
      onChange();
    }
  };

  return (
    <DatePane>
      <Label>Date: </Label>
      <Select
        style={{ width: 135 }}
        size='small'
        options={commonDateRanges}
        onChange={handleDateChange}
        value={dateOption}
        data-testid='filter-date'
        disabled={loading}
        showSearch
        disableTooltip
      />
      {dateOption === 'custom' && (
        <RangePane>
          <DatePicker
            disabled={loading}
            placeholder={['Activity From', 'Activity To']}
            type='range'
            onChange={handleDateChange}
            size='small'
            value={Array.isArray(date) ? date : undefined}
            data-testid='filter-date-range'
          />
        </RangePane>
      )}
      {showResetInfo && date === 'one' && (
        <Tooltip
          title='This filter has been updated because this page has been accessed through the Followed Organisation page.'
          placement='topRight'
        >
          <InfoItem>
            <Icon icon='circleInfo' color='red' size='sm' />
          </InfoItem>
        </Tooltip>
      )}
    </DatePane>
  );
};

OrgDateFilter.propTypes = {
  organisationId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  showResetInfo: PropTypes.bool,
};

export default OrgDateFilter;
